import type { ComponentType, ReactNode } from 'react';
import { BookOpen, FileText, LayoutDashboard, LogOut, Settings } from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import './InstructorLayout.css';

interface InstructorLayoutProps {
  children: ReactNode;
}

interface InstructorNavItem {
  label: string;
  path: string;
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
}

const instructorNavItems: InstructorNavItem[] = [
  { label: 'Overview', path: '/instructor', icon: LayoutDashboard },
  { label: 'Submissions', path: '/instructor/submissions', icon: FileText },
  { label: 'Courses', path: '/instructor/courses', icon: BookOpen },
];

const InstructorLayout = ({ children }: InstructorLayoutProps) => {
  const location = useLocation();
  const { logout } = useAuth();

  const isActivePath = (path: string) => {
    if (path === '/instructor') {
      return location.pathname === '/instructor';
    }

    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
  };

  return (
    <div className="instructor-shell">
      <aside className="instructor-sidebar" aria-label="Instructor navigation">
        <div>
          <div className="instructor-brand">
            <div className="instructor-logo" aria-hidden="true">
              ILA
            </div>
            <div className="instructor-brand-text">
              <span className="instructor-brand-name">NTU DSAIR</span>
              <span className="instructor-brand-sub">Learning Analytics</span>
            </div>
          </div>

          <nav className="instructor-nav">
            {instructorNavItems.map((item) => {
              const Icon = item.icon;

              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/instructor'}
                  className={
                    isActivePath(item.path)
                      ? 'instructor-nav-link active'
                      : 'instructor-nav-link'
                  }
                >
                  <Icon strokeWidth={2} />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </nav>
        </div>

        <div>
          <div className="instructor-sidebar-divider" />
          <div className="instructor-sidebar-bottom">
            <NavLink
              to="/settings"
              className={({ isActive }) =>
                isActive ? 'instructor-nav-link active' : 'instructor-nav-link'
              }
            >
              <Settings strokeWidth={2} />
              <span>Settings</span>
            </NavLink>
            <button
              className="instructor-nav-button instructor-logout"
              onClick={handleLogout}
              type="button"
            >
              <LogOut strokeWidth={2} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      <main className="instructor-main">{children}</main>
    </div>
  );
};

export { InstructorLayout };
